import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="w-full px-4 md:px-6 pt-24 pb-20">
          <div className="max-w-[800px] mx-auto text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-4">文章不存在</h1>
            <p className="text-foreground-600 mb-6">抱歉，您访问的文章不存在或已被删除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '看片推荐',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': typeof window !== 'undefined' ? window.location.href : '',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main>
        <section className="w-full px-4 md:px-6 pt-24 pb-8 bg-background-50">
          <div className="max-w-[900px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.categoryId === 'cat-1' ? 'free-platforms' : article.categoryId === 'cat-2' ? 'paid-platforms' : article.categoryId === 'cat-3' ? 'hd-movies' : article.categoryId === 'cat-4' ? 'variety-shows' : article.categoryId === 'cat-5' ? 'documentary' : article.categoryId === 'cat-6' ? 'anime' : article.categoryId === 'cat-7' ? 'overseas' : 'safety-guide'}` },
                { label: article.title },
              ]}
            />
          </div>
        </section>

        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-[900px] mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-3 flex-wrap mb-4">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  {article.categoryName}
                </span>
                <span className="flex items-center gap-1 text-sm text-amber-600 font-medium">
                  <i className="ri-star-fill"></i>
                  {article.rating} / 5.0
                </span>
                <span className="text-xs text-foreground-500">
                  {article.readTime}
                </span>
              </div>

              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-6 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-600 pb-8 border-b border-background-200/70">
                <span className="flex items-center gap-1">
                  <i className="ri-user-3-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  发布于 {article.publishedAt}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-refresh-line"></i>
                  更新于 {article.updatedAt}
                </span>
              </div>
            </header>

            <div className="relative aspect-[16/9] rounded-xl overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none mb-10 article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10 p-6 rounded-xl bg-background-100">
              <div>
                <h3 className="text-base font-bold text-green-700 mb-3 flex items-center gap-2 font-heading">
                  <i className="ri-thumb-up-line"></i>
                  优点
                </h3>
                <ul className="flex flex-col gap-2">
                  {article.pros.map((pro, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-green-500">
                        <i className="ri-check-line text-xs"></i>
                      </span>
                      {pro}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-base font-bold text-red-600 mb-3 flex items-center gap-2 font-heading">
                  <i className="ri-thumb-down-line"></i>
                  不足
                </h3>
                <ul className="flex flex-col gap-2">
                  {article.cons.map((con, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-red-400">
                        <i className="ri-close-line text-xs"></i>
                      </span>
                      {con}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-10">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 rounded-xl bg-background-100 mb-12">
              <div className="flex-1">
                <h4 className="text-xs text-foreground-500 mb-1">价格范围</h4>
                <p className="text-sm font-semibold text-foreground-900">{article.priceRange}</p>
              </div>
              <div className="flex-1">
                <h4 className="text-xs text-foreground-500 mb-1">最高画质</h4>
                <p className="text-sm font-semibold text-foreground-900">{article.videoQuality}</p>
              </div>
              {article.platformUrl && (
                <a
                  href={article.platformUrl}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  访问平台
                  <i className="ri-external-link-line ml-1.5"></i>
                </a>
              )}
            </div>

            {relatedArticles.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-foreground-900 mb-6 font-heading">
                  推荐阅读
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-product-shop="">
                  {relatedArticles.map((rel) => (
                    <article
                      key={rel.id}
                      className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
                    >
                      <Link to={`/article/${rel.slug}`} className="block">
                        <div className="relative aspect-[16/9] overflow-hidden">
                          <img
                            src={rel.coverImage}
                            alt={rel.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="text-sm font-bold text-foreground-900 font-heading group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                            {rel.title}
                          </h3>
                        </div>
                      </Link>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}